import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { AuthService } from '@/app/pages/service/auth/auth.service';
import { AvatarModule } from 'primeng/avatar';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { MenuItem, MessageService } from 'primeng/api';
import { UsuarioService } from '@/app/pages/service/usuario/usuario.service';
import { SharedService } from '@/app/pages/service/shared.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { STORAGE_KEYS } from '@/shared/constants/storage-keys'; 

@Component({
    selector: '[app-menu-profile]',
    standalone: true,
    imports: [
        CommonModule, 
        RippleModule, 
        AvatarModule, 
        ProgressSpinnerModule, 
        ToastModule,
        TieredMenuModule
    ],
    providers: [SharedService],
    template: `
    @if (loading) {
        <div class="progress-spinner">
            <p-progressSpinner></p-progressSpinner>
        </div>
    }

    <p-toast/>

    <div class="layout-menu-profile flex flex-row align-items-center w-full p-3 cursor-pointer mb-6 rounded-md hover:bg-[var(--sidebar-menu-item-hover)]" (click)="menuUsuario.toggle($event)">
        <input #fileInput type="file" accept="image/*" class="hidden" (change)="changeProfilePhoto($event)" />

        <div class="relative group cursor-pointer" title="Alterar foto"> <!-- (click)="fileInput.click()" -->
            @if (workspaceSelecionado?.foto) {
                <p-avatar [image]="workspaceSelecionado?.foto" shape="circle" size="large"></p-avatar>
            } @else {
                <p-avatar [label]="getInitialLetters(obterNomeWorkspace(workspaceSelecionado, false))" shape="circle" size="large" [style]="getAvatarColor(obterNomeWorkspace(workspaceSelecionado, false))"></p-avatar>
            }

            <div class="absolute inset-0 rounded-full bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <i class="pi pi-camera text-white text-lg"></i>
            </div>
        </div>

        <div class="flex flex-col ml-2 gap-1 flex-1 min-w-0">
            <span class="tracking-wide font-bold truncate">{{ obterNomeWorkspace(workspaceSelecionado, false) }}</span>
            <span class="text-sm text-muted-foreground tracking-wide truncate">
                {{ obterNomeWorkspace(workspaceSelecionado, true) }}
            </span>
        </div>

        <i class="pi pi-fw pi-angle-down ml-auto"></i>
    </div>

    <p-tieredMenu 
        #menuUsuario 
        [model]="menuPerfil" 
        [popup]="true" 
        appendTo="body" 
        [baseZIndex]="99999" 
        styleClass="menuprofile-workspace-scroll" 
        [autoDisplay]="false">
        <ng-template pTemplate="item" let-item let-hasSubmenu="hasSubmenu">
            <a pRipple class="flex gap-2 p-2 cursor-pointer p-[var(--p-menu-item-padding)]">
                @if (item.state?.workspace) {
                    <div class="flex w-full items-center justify-between">
                        <div>
                            <div class="font-medium opacity-90">
                                {{ obterNomeWorkspace(item.state.workspace, false) }}
                            </div>

                            <div class="text-sm opacity-70">
                                {{ obterNomeWorkspace(item.state.workspace, true) }}
                            </div>
                        </div>

                        @if (verificarWorkspaceSalvo(item.state.workspace)) {
                            <i class="pi pi-check shrink-0 opacity-70"></i>
                        } @else {
                            <i class="pi pi-angle-right shrink-0 opacity-50"></i>
                        }
                    </div>
                } @else {
                    <span [class]="item.icon"></span>
                    <span>{{ item.label }}</span>

                    @if (hasSubmenu) {
                        <i class="pi pi-angle-right ml-auto"></i>
                    }
                }
            </a>
        </ng-template>
    </p-tieredMenu>
    `,
    styles: [
        `
            ::ng-deep .menuprofile-workspace-scroll .p-tieredmenu-submenu {
                max-height: 32rem !important;
                overflow-y: auto !important;
                overflow-x: hidden !important;
            }
        `
    ]
})
export class AppMenuProfile {
    loading: boolean = false;

    usuario: any;
    workspaceSelecionado: any;
    listaClientes: any[] = [];

    menuPerfil: MenuItem[] = [];

    @ViewChild('menuUsuario') menuUsuario: any;
    
    constructor(
        private authService: AuthService,
        private usuarioService: UsuarioService,
        private _sharedService: SharedService,
        private messageService: MessageService,
    ) {}

    ngOnInit() {
        this.loadProfile();
        this.carregarWorkspaceSelecionado();
        this.montarMenuPerfil();
        this.listarClientes();
    }

    listarClientes(): void {
        this.loading = true;
        this._sharedService.listarClientes().subscribe({
            next: (data) => {
                this.loading = false;
                if (!data.success) {
                    this._success(data);
                    return;
                }
                this.listaClientes = data.result;
                this.montarMenuPerfil();
            }, error: (err) => {
                this.loading = false;
                this._message('Por favor, entre em contato com o administrador.', 'error', 'Ocorreu um erro!');
            }
        })
    }

    loadProfile() {
        this.usuario = this.authService.getUserLogged();
    }

    carregarWorkspaceSelecionado() {
        const workspaceStorage = localStorage.getItem(STORAGE_KEYS.WORKSPACE);
        const workspace = workspaceStorage ? JSON.parse(workspaceStorage) : null;

        if (workspace) {
            this.workspaceSelecionado = workspace;
        }
    }

    obterNomeWorkspace(workspace: any, nomeTerminal: boolean): string {
        const index = nomeTerminal ? 0 : 1;
        const nome = workspace?.nome;
        const maximoCaracteres = 20;
        const fallback = nomeTerminal ? 'Nenhum terminal' : 'Nenhum cliente';

        if (!nome) return fallback;

        const workspaceName = nome.split(' - ')[index]?.trim();

        if (!workspaceName) return fallback;

        return workspaceName.length > maximoCaracteres
            ? `${workspaceName.slice(0, maximoCaracteres)}...`
            : workspaceName;
    }

    montarMenuPerfil(): void {
        const nenhumWorkspace = [
            {
                label: 'Nenhum workspace encontrado',
                disabled: true
            }
        ];

        let workspaces = this.listaClientes.map((cliente) => ({
            label: this.obterNomeWorkspace(cliente, false),
            state: { workspace: cliente },
            command: () => this.selecionarWorkspace(cliente)
        }));

        const itensWorkspace = workspaces.length ? workspaces : nenhumWorkspace;

        this.menuPerfil = [
            {
                label: 'Mudar Workspace',
                icon: 'pi pi-briefcase',
                items: itensWorkspace
            },
            {
                label: 'Sair',
                icon: 'pi pi-sign-out',
                command: () => this.logout()
            }
        ];
    }

    verificarWorkspaceSalvo(cliente: any): boolean {
        const workspaceStorage = localStorage.getItem(STORAGE_KEYS.WORKSPACE);
        const workspaceSalvo = workspaceStorage ? JSON.parse(workspaceStorage) : null;

        return workspaceSalvo &&
            workspaceSalvo.clienteId === cliente.clienteId &&
            workspaceSalvo.entidadeId === cliente.entidadeId;
    }

    salvarNovoWorkspace(cliente: any): void {
        const workspace = {
            clienteId: cliente.clienteId,
            entidadeId: cliente.entidadeId,
            nome: cliente.nome,
            perfil: 0
        };

        localStorage.setItem(STORAGE_KEYS.WORKSPACE, JSON.stringify(workspace));
    }

    selecionarWorkspace(cliente: any): void {
        if (!this.verificarWorkspaceSalvo(cliente)) {
            this.salvarNovoWorkspace(cliente);
        }

        window.location.reload();
    }

    logout() {
        this.authService.logout();
    }

    getInitialLetters(nome?: string | null): string {
        if (!nome) return '?';

        const partesNome = nome
            .trim()
            .split(' ')
            .filter(Boolean);

        if (partesNome.length === 0) return '?';

        const primeiraLetra = partesNome[0].charAt(0);

        if (partesNome.length === 1) {
            return primeiraLetra.toUpperCase();
        }

        const segundaLetra = partesNome[partesNome.length - 1].charAt(0);

        return `${primeiraLetra}${segundaLetra}`.toUpperCase();
    }

    getAvatarColor(nome?: string | null): { [klass: string]: string } {
        const cores = [
            { backgroundColor: '#312e81', color: '#ffffff' },
            { backgroundColor: '#1d4ed8', color: '#ffffff' },
            { backgroundColor: '#034553', color: '#ffffff' },
            { backgroundColor: '#b45309', color: '#ffffff' },
            { backgroundColor: '#b91c1c', color: '#ffffff' },
            { backgroundColor: '#be185d', color: '#ffffff' },
            { backgroundColor: '#7c3aed', color: '#ffffff' },
            { backgroundColor: '#0f766e', color: '#ffffff' },
            { backgroundColor: '#0369a1', color: '#ffffff' },
            { backgroundColor: '#c2410c', color: '#ffffff' },
        ];

        if (!nome) return cores[0];

        const primeiraLetra = nome.trim().charAt(0).toUpperCase();

        const index = primeiraLetra.charCodeAt(0) % cores.length;

        return cores[index];
    }

    changeProfilePhoto(event: any): void {
        const file: File = event.target.files[0]; 
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                this.usuario.foto = reader.result as string;
                this.saveProfilePhoto(reader.result);
            };
            reader.readAsDataURL(file);
        }
    }

    saveProfilePhoto(result: any) {
        this.usuarioService.trocarFoto(this.usuario).subscribe({
            next: (data) => {
                this._success(data);
            }, error: (err) => {
                this._message('Ocorreu um erro ao salvar a foto.', 'error', 'Erro');
            }
        })
    }

    _success(data: any) {
        data.messages.forEach((msg: any) => {
            if (msg.applicationMessageType === 0) {
                this._message(msg.message, "success", "Sucesso");
            }

            if (msg.applicationMessageType === 1) {
               this._message(msg.message, "error", "Erro");
            }

            if (msg.applicationMessageType === 2) {
                this._message(msg.message, "warn", "Alerta");
            }
        })
    }

    _message(message: string, severity: string, summary: string) {
         this.messageService.add({
            severity: severity,
            summary: summary,
            detail: message,
        });
    }
}